import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { billingService, type SellablePlanId } from '@/shared/services/billing.service';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';

export const BILLING_KEY = 'billing';

/**
 * Everything that shows the account's plan (ADR-006): billing (subscription,
 * plans, invoices), GET /accounts/me (the add-brand limits) and the brand list,
 * which carries the account subscription the banners and switcher read.
 * Every plan-changing action calls this, so an upgrade shows at once.
 */
export function invalidateAccountPlan(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [BILLING_KEY] });
  void queryClient.invalidateQueries({ queryKey: ['accounts', 'me'] });
  void queryClient.invalidateQueries({ queryKey: ['workspaces', 'me'] });
}

export function usePlans() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [BILLING_KEY, 'plans', wsId],
    queryFn: () => billingService.getPlans(),
    staleTime: 5 * 60 * 1000, // plans rarely change
    enabled: !!wsId,
  });
}

/** What the next renewal charges, tax included (Task 4.1); null when nothing renews. */
export function useNextCharge(enabled = true) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [BILLING_KEY, 'next-charge', wsId],
    queryFn: () => billingService.getNextCharge(),
    enabled: enabled && !!wsId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useSubscription() {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [BILLING_KEY, 'subscription', wsId],
    queryFn: () => billingService.getCurrentSubscription(),
    enabled: !!wsId,
    // Subscription state changes via mutation (upgrade/cancel) — those
    // invalidate the cache directly. Pin stale to 5 min to avoid hammering
    // /subscriptions/me on every page mount.
    staleTime: 5 * 60 * 1000,
  });
}

/** Invoices are the account owner's and a billing admin's (403 for anyone else): pass enabled=false for other roles. */
export function useInvoices(enabled = true) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [BILLING_KEY, 'invoices', wsId],
    queryFn: () => billingService.getInvoices(),
    enabled: !!wsId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Each charge's refund state (Task 4.2); the owner's and a billing admin's, like invoices. */
export function useRefundRequests(enabled = true) {
  const wsId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: [BILLING_KEY, 'refund-requests', wsId],
    queryFn: () => billingService.getRefundRequests(),
    enabled: !!wsId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRequestRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: string; reason?: string }) => billingService.requestRefund(invoiceId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [BILLING_KEY, 'refund-requests'] }),
  });
}

export function useCreateCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, interval }: { planId: SellablePlanId; interval: 'monthly' | 'yearly' }) =>
      billingService.createCheckout(planId, interval),
    // An open checkout is recorded on the subscription (pendingPlan).
    onSuccess: () => invalidateAccountPlan(queryClient),
  });
}

export function useConfirmPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      razorpayPaymentId,
      razorpaySignature,
      subscriptionId,
    }: {
      razorpayPaymentId: string;
      razorpaySignature: string;
      subscriptionId: string;
    }) => billingService.confirmPayment(razorpayPaymentId, razorpaySignature, subscriptionId),
    onSuccess: () => invalidateAccountPlan(queryClient),
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => billingService.cancelSubscription(),
    onSuccess: () => invalidateAccountPlan(queryClient),
  });
}
